import type { ColorParams } from '@bynder-studio/misc';
import { compareByRenderOrder, getAllElementsRecursively } from '../../Helpers/elementUtils';
import type { IAsset } from '../Assets/IAsset';
import { Dimension } from '../Shared/Dimension';
import { BaseCompElement } from './Elements/BaseCompElement';
import { getBoundingBoxAfterRotation } from '../Elements/Helpers/GroupElementHelpers';
import { Ranges } from '../../Helpers/Ranges';
import { MaskModeTypes } from '../../Enums/MaskModeTypes';
import {
    type CompElement,
    type CompModelParams,
    FunctionalCompElement,
    ControllableElement,
    VisualCompElement,
} from '../../types';

export class CompModel {
    private compVisualElements: VisualCompElement[] = [];

    private compFunctionalElements: FunctionalCompElement[] = [];

    private backgroundColor!: ColorParams;

    private frameIndex!: number;

    private width!: number;

    private height!: number;

    private _isUpToDate = true;

    private maskElementRanges!: Map<string, Ranges<MaskModeTypes>>;

    constructor() {
        this.compVisualElements = [];
        this.compFunctionalElements = [];
    }

    setFrame(frameIndex: number) {
        this.frameIndex = frameIndex;

        return this;
    }

    getFrame(): number {
        return this.frameIndex;
    }

    setDimensions(dimension: Dimension) {
        const { width, height } = dimension;
        this.width = width;
        this.height = height;

        return this;
    }

    getDimension(): Dimension {
        return new Dimension(this.width, this.height);
    }

    setBackgroundColor(backgroundColor: ColorParams) {
        this.backgroundColor = backgroundColor;

        return this;
    }

    getBackgroundColor(): ColorParams {
        return this.backgroundColor;
    }

    setElements(elements: ControllableElement[]) {
        this.compVisualElements = [];
        this.compFunctionalElements = [];

        elements.sort(compareByRenderOrder).forEach((el) => {
            const compEl = el.getCompElement(this.frameIndex);

            if (compEl) {
                if (compEl instanceof BaseCompElement) {
                    this.compVisualElements.push(compEl);
                } else {
                    this.compFunctionalElements.push(compEl);
                }
            }
        });

        return this;
    }

    setVisualCompElements(elements: VisualCompElement[]) {
        this.compVisualElements = elements;

        return this;
    }

    setMaskElementRanges(maskElementRanges: Map<string, Ranges<MaskModeTypes>>) {
        this.maskElementRanges = maskElementRanges;

        return this;
    }

    getCompVisualElements(): VisualCompElement[] {
        return this.compVisualElements;
    }

    getCompFunctionalElements(): FunctionalCompElement[] {
        return this.compFunctionalElements;
    }

    /**
     * Default value is 'visual' since it mostly used for visual logic
     * @param type 'visual' | 'functional' | 'all' = 'visual',
     */
    getAllCompElementsRecursively<T extends CompElement = CompElement>(
        type: 'visual' | 'functional' | 'all' = 'visual',
    ): T[] {
        if (type === 'visual') {
            return getAllElementsRecursively<VisualCompElement>(this.compVisualElements) as T[];
        } else if (type === 'functional') {
            return getAllElementsRecursively<FunctionalCompElement>(this.compFunctionalElements) as T[];
        }

        return getAllElementsRecursively<CompElement>([
            ...this.compVisualElements,
            ...this.compFunctionalElements,
        ]) as T[];
    }

    invalidate(): void {
        this._isUpToDate = false;
    }

    isContainsAsset(asset: IAsset): boolean {
        return [...this.getCompVisualElements(), ...this.getCompFunctionalElements()].some((compElement) =>
            compElement.isContainsAsset(asset),
        );
    }

    isElementMask(elementId: number | string): boolean {
        const key = elementId.toString();

        return this.maskElementRanges.has(key);
    }

    isElementMaskEnabled(elementId: number | string): boolean {
        const key = elementId.toString();
        const ranges = this.maskElementRanges.get(key);

        if (!ranges) {
            return false;
        }

        const mode = ranges.get(this.frameIndex);

        if (!mode) {
            return false;
        }

        return [MaskModeTypes.ALPHA, MaskModeTypes.ALPHA_INVERTED].includes(mode);
    }

    isElementHiddenBecauseOfMask(element: BaseCompElement): boolean {
        const key = element.id.toString();

        // The mask could be used as a part of rendering prcoess for some features.
        // These types of masks are not included in the mask ranges set by user
        if (element.usedAsInternalMask) {
            return true;
        }

        const ranges = this.maskElementRanges.get(key);

        if (!ranges) {
            return false;
        }

        const mode = ranges.get(this.frameIndex);

        return mode === null || mode !== MaskModeTypes.NONE;
    }

    isUpToDate(): boolean {
        return this._isUpToDate;
    }

    getBoundingBox(): { x: number; y: number; width: number; height: number } {
        let xMin = Number.MAX_SAFE_INTEGER;
        let yMin = Number.MAX_SAFE_INTEGER;
        let xMax = Number.MIN_SAFE_INTEGER;
        let yMax = Number.MIN_SAFE_INTEGER;

        this.compVisualElements.forEach((compElement) => {
            const points = getBoundingBoxAfterRotation((compElement as unknown as BaseCompElement).originalElement);
            xMin = Math.min(xMin, ...points.map((p) => p.x));
            yMin = Math.min(yMin, ...points.map((p) => p.y));
            xMax = Math.max(xMax, ...points.map((p) => p.x));
            yMax = Math.max(yMax, ...points.map((p) => p.y));
        });

        return {
            x: xMin,
            y: yMin,
            width: xMax - xMin,
            height: yMax - yMin,
        };
    }

    toObject(): CompModelParams {
        return {
            frameIndex: this.frameIndex,
            // NOTE: No functional elements yet, do we need them here?
            elements: this.compVisualElements.map((el) => (el as unknown as BaseCompElement).toObject()),
            backgroundColor: this.backgroundColor,
            width: this.width,
            height: this.height,
        };
    }
}
